import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AdminTooltip, CYAN, MAGENTA, RING } from '../shell/ui';
import { useAdminShell } from '../shell/AdminShellContext';

/**
 * The panel's charts, to the Portal handoff. One measure per chart, recessive dashed
 * gridlines, y and x labels as pills, and ONE tooltip component (`AdminTooltip`) shared
 * by tiles, bars, columns, y-axis labels and x ticks — add a breakdown or a drill here,
 * never in a page.
 *
 * Colour is fixed by the handoff: bars are solid magenta and the hovered bar is solid
 * cyan (never a gradient); the line's stroke is the ring gradient over a lavender fill,
 * and a second series is a dashed grey line.
 */

export const INK = '#000000';
export const MUTED = '#9CA3AF';
export const GRID = '#EEEEEE';
export const LAVENDER = '#B5B1FF';

export const fmt = (n) => (n ?? 0).toLocaleString();
export const hms = (sec) => {
  const s = Math.max(0, Math.round(sec || 0));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
};
const niceMax = (max) => {
  if (max <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(max));
  const m = max / p;
  const step = m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10;
  return step * p;
};
const shortDay = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleString('en', { month: 'short' }).toUpperCase()} ${d.getDate()}`;
};

/* ------------------------------------------------------------------ tooltip */

/**
 * Every chart tooltip is ONE floating box, portalled to <body> and placed in viewport
 * coordinates. Two rules come out of that, and both were bugs before it existed:
 *
 * 1. **It is never inside the chart.** A tooltip positioned inside the plot was clipped
 *    by the card, and inside the column chart's horizontal scroller it made the chart
 *    itself scroll sideways. In the body it can overhang the whole card.
 * 2. **It never covers the data.** It is placed BELOW the chart's own band (above it
 *    when the viewport has no room below), centred on the hovered bar or day, so every
 *    other bar stays hoverable while it is open.
 *
 * It stays open while the pointer is inside it, which is what makes the drill button
 * (SEE WHO VISITED → / OPEN THE PERSON →) clickable at all.
 */
const TIP_W = 280;
const TIP_GAP = 10;
const TIP_EDGE = 10;

/** Hover with a grace period, so the pointer can travel from the chart into the tooltip. */
function useHoverTip() {
  const [tip, setTip] = useState(null); // { kind: 'item' | 'axis', i }
  const timer = useRef(null);
  const show = useCallback((next) => { clearTimeout(timer.current); setTip(next); }, []);
  const hide = useCallback(() => { clearTimeout(timer.current); timer.current = setTimeout(() => setTip(null), 160); }, []);
  const hold = useCallback(() => clearTimeout(timer.current), []);
  // A drill opens something on top of the chart, so the tooltip goes at once — a fade
  // would leave it floating over the sheet it just opened.
  const close = useCallback(() => { clearTimeout(timer.current); setTip(null); }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  return { tip, show, hide, hold, close };
}

/**
 * The two rectangles the tooltip is placed from: the chart's band (what it must stay
 * out of) and the hovered item (what it points at). Re-measured on scroll with capture
 * on, so an inner scroller — the person drawer, the bar strip — counts too.
 */
function useTipGeometry(open) {
  const bandRef = useRef(null);
  const itemRef = useRef(null);
  const [geo, setGeo] = useState(null);
  const measure = useCallback(() => {
    const band = bandRef.current;
    if (!band || typeof band.getBoundingClientRect !== 'function') return;
    const b = band.getBoundingClientRect();
    const it = itemRef.current?.getBoundingClientRect?.() || null;
    const next = {
      top: b.top, bottom: b.bottom, left: b.left, right: b.right,
      x: it ? it.left + it.width / 2 : b.left + b.width / 2,
      y: it ? it.top + it.height / 2 : b.top + b.height / 2,
    };
    setGeo((prev) => (prev && Object.keys(next).every((k) => Math.abs(prev[k] - next[k]) < 0.5) ? prev : next));
  }, []);
  useEffect(() => {
    if (!open) return undefined;
    measure();
    const on = () => measure();
    window.addEventListener('scroll', on, true);
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on, true); window.removeEventListener('resize', on); };
  }, [open, measure]);
  return { bandRef, itemRef, geo, measure };
}

const clamp = (v, lo, hi) => Math.max(lo, Math.min(Math.max(lo, hi), v));

/**
 * The floating box itself. `place="below"` keeps clear of the chart band (bars stay
 * hoverable); `place="side"` sits beside it, which is what a full-width bar row wants.
 */
export function ChartTip({ geo, place = 'below', width = TIP_W, onHold, onRelease, children }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !geo) return;
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const h = el.offsetHeight || 0;
    const w = Math.min(width, Math.max(160, vw - TIP_EDGE * 2));
    let left;
    let top;
    if (place === 'side') {
      const toRight = geo.right + TIP_GAP;
      const toLeft = geo.left - TIP_GAP - w;
      left = toRight + w <= vw - TIP_EDGE ? toRight : (toLeft >= TIP_EDGE ? toLeft : clamp(geo.x - w / 2, TIP_EDGE, vw - w - TIP_EDGE));
      top = clamp(geo.y - h / 2, TIP_EDGE, vh - h - TIP_EDGE);
    } else {
      const below = geo.bottom + TIP_GAP;
      const above = geo.top - TIP_GAP - h;
      top = below + h <= vh - TIP_EDGE ? below : (above >= TIP_EDGE ? above : clamp(below, TIP_EDGE, vh - h - TIP_EDGE));
      left = clamp(geo.x - w / 2, TIP_EDGE, vw - w - TIP_EDGE);
    }
    setPos((prev) => (prev && prev.left === left && prev.top === top && prev.w === w ? prev : { left, top, w }));
    // `children` is in here on purpose: a tooltip that grows a breakdown is a different
    // height, and the placement is decided from that height. The setter bails when the
    // numbers come out the same, so re-running every render costs nothing.
  }, [geo, width, place, children]);
  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      ref={ref}
      data-chart-tip
      onMouseEnter={onHold}
      onMouseLeave={onRelease}
      style={{
        position: 'fixed',
        zIndex: 200,
        left: pos ? pos.left : -9999,
        top: pos ? pos.top : -9999,
        width: pos ? pos.w : width,
        maxHeight: `calc(100vh - ${TIP_EDGE * 2}px)`,
        overflowY: 'auto',
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}

/** Measure a chart's own width so the x ticks can decide how much to say. */
function useWidth() {
  const [w, setW] = useState(0);
  const el = useRef(null);
  const ref = useCallback((node) => {
    el.current = node;
    if (!node) return;
    setW(Math.round(node.getBoundingClientRect().width));
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((e) => setW(Math.round(e[0].contentRect.width)));
    ro.observe(node);
  }, []);
  return [w, ref, el];
}

/** The least a day may have on a phone before the chart scrolls instead of squeezing. */
const DAY_PX = 28;

/**
 * A phone cannot show 30 days in 330px and still be read, let alone tapped. When the days
 * get thinner than 12px the plot becomes a sideways scroller at DAY_PX per day — and it
 * opens on TODAY (scrolled to the end), because the newest day is the one being checked;
 * the past is a flick to the left.
 */
function useJumpToToday(el, active, dep) {
  useLayoutEffect(() => {
    const node = el.current;
    if (!node || !active) return;
    node.scrollLeft = node.scrollWidth;
  }, [el, active, dep]);
}

function Legend({ items, right }) {
  if (!items?.length && !right) return null;
  return (
    <div className="flex flex-wrap items-center gap-[6px]">
      {(items || []).map((l) => (
        <span key={l.label} className="flex h-7 items-center gap-2 rounded-full bg-[#F2F2F2] px-3 font-mono text-[9px] font-bold uppercase tracking-[0.12em]">
          <span className="h-1 w-[14px] rounded-full" style={{ background: l.bg || MAGENTA }} />{l.label}
        </span>
      ))}
      {right && <span className="pl-[6px] font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">{right}</span>}
    </div>
  );
}

/**
 * The y-axis: label pills, the hovered one black. The axis tooltip is the chart's own
 * floating one — the axis reports which tick is under the pointer and nothing else.
 */
function YAxis({ ticks, format, height, axisInfo, width = 56, active, onTick, onLeave }) {
  const top = ticks[ticks.length - 1] || 1;
  return (
    <div className="relative flex-shrink-0" style={{ width, height }} onMouseLeave={axisInfo ? onLeave : undefined}>
      {ticks.map((t, i) => {
        const on = active === i;
        return (
          <span
            key={t}
            onMouseEnter={axisInfo ? (e) => onTick(i, e.currentTarget) : undefined}
            onClick={axisInfo ? (e) => onTick(i, e.currentTarget) : undefined}
            className={`absolute right-[10px] -translate-y-1/2 whitespace-nowrap rounded-full px-[7px] py-[2px] font-mono text-[10px] leading-[1.3] transition-colors duration-200 ${on ? 'bg-black font-bold text-white' : 'text-[#9CA3AF]'} ${axisInfo ? 'cursor-help' : ''}`}
            style={{ top: `${(1 - t / top) * 100}%` }}
          >
            {format(t)}
          </span>
        );
      })}
    </div>
  );
}

/**
 * LineChart — series: [{ key, label, format? }], points: [{ date, ...values }].
 * The first series is the ring-gradient stroke over the lavender fill; a second is the
 * dashed grey line. `breakdownFor(i)`, `source`, `drill` and `onDrill(i)` fill the
 * tooltip; `axisInfo` names what the axis counts.
 */
export function LineChart({
  points, series, height = 220, format = fmt, label = 'Chart', title, right, foot,
  breakdownFor, source, drill, onDrill, axisInfo, tipTitleFor,
}) {
  const { tip: tipState, show, hide, hold, close } = useHoverTip();
  const { bandRef, itemRef, geo, measure } = useTipGeometry(!!tipState);
  const hover = tipState?.kind === 'item' ? tipState.i : null;
  const axisTick = tipState?.kind === 'axis' ? tipState.i : null;
  const point = useCallback((kind, i, el) => { itemRef.current = el || null; show({ kind, i }); measure(); }, [itemRef, show, measure]);
  const { isMobile } = useAdminShell();
  const [plotWidth, plotRef, plotEl] = useWidth();
  const gid = `pr-line-${useMemo(() => Math.random().toString(36).slice(2, 8), [])}`;
  const fid = `${gid}-fill`;
  const n = points.length;
  const max = niceMax(Math.max(1, ...points.flatMap((p) => series.map((s) => p[s.key] || 0))));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const axisW = 56;
  // Scrolls on a phone once the days are too thin to read; opens on today.
  const scrolls = isMobile && plotWidth > 0 && n * 12 > plotWidth;
  const plotW = scrolls ? Math.max(plotWidth, n * DAY_PX) : undefined;
  useJumpToToday(plotEl, scrolls, plotW);
  if (n === 0) return <div className="py-8 text-center font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">NO DATA IN THIS RANGE.</div>;

  const xAt = (i) => (n <= 1 ? 0 : (i / (n - 1)) * 1000);
  const yAt = (v) => 100 - (v / max) * 100;
  const path = (key) => points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(p[key] || 0).toFixed(2)}`).join(' ');
  const area = `${path(series[0].key)} L1000,100 L0,100 Z`;
  const labelEvery = scrolls ? Math.max(1, Math.ceil(64 / DAY_PX)) : Math.max(1, Math.ceil(n / 8));
  const showLabel = (i) => i === n - 1 || (i % labelEvery === 0 && n - 1 - i >= Math.ceil(labelEvery / 2));
  const tip = hover != null ? {
    title: tipTitleFor ? tipTitleFor(hover) : shortDay(points[hover].date),
    rows: series.map((s, si) => ({ label: s.label, value: (s.format || format)(points[hover][s.key] || 0), color: si === 0 ? MAGENTA : '#BDBDBD' })),
    ...(breakdownFor ? breakdownFor(hover) || {} : {}),
  } : null;

  return (
    <div className="min-w-0" onMouseLeave={hide}>
      {(title || right || series.length > 1) && (
        <div className="mb-[18px] flex flex-wrap items-center justify-between gap-x-4 gap-y-[10px]">
          {title && <span className="font-mono text-[12px] font-bold uppercase tracking-[0.16em]">{title}</span>}
          <Legend items={series.map((s, si) => ({ label: s.label, bg: si === 0 ? RING : '#BDBDBD' }))} right={right} />
        </div>
      )}
      {/* The band the tooltip keeps clear of: the plot and the day labels under it. */}
      <div ref={bandRef} className="flex">
        <YAxis ticks={ticks} format={format} height={height} axisInfo={axisInfo} width={axisW} active={axisTick} onTick={(i, el) => point('axis', i, el)} onLeave={hide} />
        <div ref={plotRef} className={`min-w-0 flex-1 ${scrolls ? 'pr-scroll-x' : ''}`} data-chart-scroll={scrolls ? 'true' : undefined}>
          <div style={{ width: plotW, paddingRight: scrolls ? 28 : 0 }}>
          <div className="relative" style={{ height }}>
            {ticks.map((t, i) => (
              <span key={t} className="pointer-events-none absolute left-0 right-0" style={{ top: `${(1 - t / max) * 100}%`, borderTop: i === 0 ? `2px solid ${GRID}` : `1px dashed ${GRID}` }} />
            ))}
            <svg viewBox="0 0 1000 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full overflow-visible" role="img" aria-label={label}>
              <defs>
                <linearGradient id={gid} x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#FD199F" /><stop offset=".33" stopColor="#B5B1FF" /><stop offset=".66" stopColor="#127BFD" /><stop offset="1" stopColor="#22ECFB" />
                </linearGradient>
                <linearGradient id={fid} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor={LAVENDER} stopOpacity=".32" /><stop offset="1" stopColor={LAVENDER} stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={area} fill={`url(#${fid})`} />
              {series.slice(1).map((s) => (
                <path key={s.key} d={path(s.key)} fill="none" stroke="#BDBDBD" strokeWidth="2" strokeDasharray="6 5" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              ))}
              <path d={path(series[0].key)} fill="none" stroke={`url(#${gid})`} strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
            </svg>
            {points.map((p, i) => (
              <span
                key={p.date ?? i}
                onMouseEnter={(e) => point('item', i, e.currentTarget)}
                onClick={(e) => point('item', i, e.currentTarget)}
                className="absolute bottom-0 top-0 z-[2] cursor-pointer"
                style={{ left: `${(i / Math.max(1, n)) * 100}%`, width: `${100 / Math.max(1, n)}%` }}
              >
                {hover === i && <span className="absolute bottom-0 left-1/2 top-0 -ml-px w-[2px] rounded-full bg-[#E5E7EB]" />}
              </span>
            ))}
            {hover != null && series.map((s, si) => (
              <span
                key={s.key}
                className="pointer-events-none absolute z-[3] h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{ left: `${xAt(hover) / 10}%`, top: `${yAt(points[hover][s.key] || 0)}%`, background: si === 0 ? MAGENTA : '#BDBDBD', boxShadow: '0 0 0 3px #fff' }}
              />
            ))}
          </div>
          <div className="relative h-[34px]">
            {points.map((p, i) => showLabel(i) && (
              <span
                key={p.date ?? i}
                onMouseEnter={(e) => point('item', i, e.currentTarget)}
                className={`absolute top-2 cursor-pointer whitespace-nowrap rounded-full px-2 py-[3px] font-mono text-[10px] transition-colors duration-200 ${i === 0 && scrolls ? '' : '-translate-x-1/2'} ${hover === i ? 'bg-black font-bold text-white' : 'text-[#9CA3AF]'}`}
                style={{ left: `${xAt(i) / 10}%` }}
              >
                {shortDay(p.date)}
              </span>
            ))}
          </div>
          </div>
        </div>
      </div>
      {foot && <div className="pb-1 pt-2 font-mono text-[10px] leading-[1.5] text-[#9CA3AF]">{foot}</div>}
      {tip && geo && (
        <ChartTip geo={geo} onHold={hold} onRelease={hide}>
          <AdminTooltip {...tip} source={source} drill={drill} onDrill={onDrill ? () => { close(); onDrill(hover); } : undefined} />
        </ChartTip>
      )}
      {axisInfo && axisTick != null && geo && (
        <ChartTip geo={geo} onHold={hold} onRelease={hide}>
          <AdminTooltip title={axisInfo.title} kicker={axisInfo.kicker} desc={axisInfo.desc} breakdownLabel={axisInfo.breakdownLabel} breakdown={axisInfo.breakdown} source={axisInfo.source} />
        </ChartTip>
      )}
    </div>
  );
}

/**
 * ColumnChart — one measure per item. items: [{ key, label, value, avatarUrl, initials,
 * tip: [[label, value]], breakdown?, source? }]. Bars are magenta; the hovered bar is
 * cyan. `onSelect` fires on the SECOND click of the same bar (the first shows its
 * tooltip), which is how the person behind a bar is opened.
 */
export function ColumnChart({ items, format = fmt, height = 180, onSelect, axisInfo, source, drill }) {
  const { tip: tipState, show, hide, hold, close } = useHoverTip();
  const { bandRef, itemRef, geo, measure } = useTipGeometry(!!tipState);
  const hover = tipState?.kind === 'item' ? tipState.i : null;
  const axisTick = tipState?.kind === 'axis' ? tipState.i : null;
  const point = useCallback((kind, i, el) => { itemRef.current = el || null; show({ kind, i }); measure(); }, [itemRef, show, measure]);
  const [w, ref] = useWidth();
  const { isMobile } = useAdminShell();
  const scroller = useRef(null);
  const max = niceMax(Math.max(1, ...items.map((i) => i.value || 0)));
  const ticks = [0, 0.5, 1].map((f) => f * max);
  // Avatar ticks are the VISITS BY PERSON chart and only name people on a wide screen.
  // A chart whose items are days has no avatar and must always say which day it is —
  // every second day when they are packed at 40px, and always the last one (today).
  const hasAvatars = items.some((i) => i.avatarUrl);
  const showNames = w >= 1400 || (!hasAvatars && items.length <= 16);
  const last = items.length - 1;
  const dayLabel = (i) => showNames || (!hasAvatars && (i === last || (i % 2 === 0 && last - i >= 2)));
  const barR = items.length > 40 ? '5px 5px 2px 2px' : '5px 5px 2px 2px';
  // The strip is wider than the card on a phone: open it on today, the past is a flick left.
  useJumpToToday(scroller, items.length * 40 > w - 48, items.length);
  if (items.length === 0) return <div className="py-8 text-center font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">NO ONE IN THIS RANGE.</div>;
  return (
    <div ref={ref} className="min-w-0" onMouseLeave={hide}>
      {/* The band the tooltip keeps clear of, so every other bar stays hoverable. */}
      <div ref={bandRef} className="flex">
        <YAxis ticks={ticks} format={format} height={height} axisInfo={axisInfo} width={48} active={axisTick} onTick={(i, el) => point('axis', i, el)} onLeave={hide} />
        <div ref={scroller} className={`relative min-w-0 flex-1 ${isMobile ? 'pr-scroll-x' : 'overflow-x-auto'}`} data-chart-scroll="true">
          <div className="relative" style={{ height, minWidth: items.length * 40 }}>
            {ticks.map((t, i) => (
              <span key={t} className="pointer-events-none absolute left-0 right-0" style={{ top: `${(1 - t / max) * 100}%`, borderTop: i === 0 ? `2px solid ${GRID}` : `1px dashed ${GRID}` }} />
            ))}
            <div className="absolute inset-0 flex items-end">
              {items.map((it, i) => (
                <button
                  key={it.key}
                  type="button"
                  onMouseEnter={(e) => point('item', i, e.currentTarget)}
                  onClick={(e) => { if (hover === i && onSelect) { close(); onSelect(it); } else point('item', i, e.currentTarget); }}
                  className="flex h-full min-w-0 flex-1 items-end justify-center"
                  aria-label={`${it.label}: ${format(it.value)}`}
                >
                  <span className="w-[60%] max-w-[26px] transition-colors duration-200" style={{ height: `${Math.max(2, ((it.value || 0) / max) * 100)}%`, borderRadius: barR, background: hover === i ? CYAN : MAGENTA }} />
                </button>
              ))}
            </div>
          </div>
          <div className="relative h-[34px]" style={{ minWidth: items.length * 40 }}>
            {items.map((it, i) => (
              <span
                key={it.key}
                onMouseEnter={(e) => point('item', i, e.currentTarget)}
                className={`absolute top-2 flex -translate-x-1/2 items-center gap-[5px] whitespace-nowrap rounded-full px-2 py-[3px] font-mono text-[10px] transition-colors duration-200 ${hover === i ? 'bg-black font-bold text-white' : 'text-[#9CA3AF]'}`}
                style={{ left: `${((i + 0.5) / items.length) * 100}%` }}
              >
                {it.avatarUrl ? <img src={it.avatarUrl} alt="" className="block h-[18px] w-[18px] rounded-full object-cover" /> : null}
                {dayLabel(i) ? (hasAvatars ? (it.label || '').split(' ')[0] : it.label) : null}
              </span>
            ))}
          </div>
        </div>
      </div>
      {hover != null && geo && (
        <ChartTip geo={geo} onHold={hold} onRelease={hide}>
          <AdminTooltip
            title={items[hover].label}
            kicker={format(items[hover].value)}
            rows={(items[hover].tip || []).map(([l, v]) => ({ label: l, value: v }))}
            breakdownLabel={items[hover].breakdownLabel}
            breakdown={items[hover].breakdown}
            source={items[hover].source || source}
            drill={onSelect ? (drill || 'OPEN →') : undefined}
            onDrill={onSelect ? () => { close(); onSelect(items[hover]); } : undefined}
          />
        </ChartTip>
      )}
      {axisInfo && axisTick != null && geo && (
        <ChartTip geo={geo} onHold={hold} onRelease={hide}>
          <AdminTooltip title={axisInfo.title} kicker={axisInfo.kicker} desc={axisInfo.desc} breakdownLabel={axisInfo.breakdownLabel} breakdown={axisInfo.breakdown} source={axisInfo.source} />
        </ChartTip>
      )}
    </div>
  );
}

/**
 * The bars block — rows with a label, a value and a 6px track with a magenta fill
 * (cyan on hover). items: [{ label, count, image, imageRound, tip?, onSelect? }].
 */
export function BarList({ items, format = fmt, max: maxOverride, source, drill, onSelect }) {
  const { tip: tipState, show, hide, hold, close } = useHoverTip();
  const { bandRef, itemRef, geo, measure } = useTipGeometry(!!tipState);
  const hover = tipState?.kind === 'item' ? tipState.i : null;
  // A row is full width, so the tooltip goes BESIDE the list, not over the rows under it.
  const point = useCallback((i, el) => { bandRef.current = el || null; itemRef.current = el || null; show({ kind: 'item', i }); measure(); }, [bandRef, itemRef, show, measure]);
  if (!items || items.length === 0) return <div className="py-6 text-center font-mono text-[10px] uppercase tracking-[0.1em] text-[#9CA3AF]">NO DATA IN THIS RANGE.</div>;
  const max = maxOverride || Math.max(1, ...items.map((i) => i.count));
  const total = items.reduce((s, i) => s + i.count, 0) || 1;
  const it = hover != null ? items[hover] : null;
  // The drill is the row's own handler when it has one, the list's otherwise; either way
  // the tooltip closes first, so it is not left floating over whatever the drill opened.
  let onDrillRow;
  if (it?.onSelect) onDrillRow = () => { close(); it.onSelect(); };
  else if (it && onSelect) onDrillRow = () => { close(); onSelect(it); };
  return (
    <div className="flex flex-col" onMouseLeave={hide}>
      {items.map((row, i) => (
        <div
          key={`${row.label}-${i}`}
          onMouseEnter={(e) => point(i, e.currentTarget)}
          onClick={row.onSelect || (onSelect ? () => onSelect(row) : undefined)}
          className={`relative flex flex-col gap-[6px] rounded-[14px] px-2 py-[9px] transition-colors duration-200 hover:bg-[#F7F7F7] ${row.onSelect || onSelect ? 'cursor-pointer' : ''}`}
        >
          <div className="flex items-center justify-between gap-[10px]">
            <span className="flex min-w-0 items-center gap-2">
              {row.image ? <img src={row.image} alt="" className="h-[22px] w-[22px] flex-shrink-0 object-cover" style={{ borderRadius: row.imageRound === false ? 6 : 999 }} /> : null}
              <span className="min-w-0 truncate font-mono text-[11px] font-semibold tracking-[0.04em]">{row.label || '—'}</span>
            </span>
            <span className="whitespace-nowrap font-mono text-[12px] font-extrabold">{format(row.count)}</span>
          </div>
          <div className="h-[6px] rounded-full bg-[#F2F2F2]">
            <div className="h-full rounded-full transition-colors duration-200" style={{ width: `${(row.count / max) * 100}%`, background: hover === i ? CYAN : MAGENTA }} />
          </div>
        </div>
      ))}
      {it && geo && (it.tip || it.breakdown) && (
        <ChartTip geo={geo} place="side" onHold={hold} onRelease={hide}>
          <AdminTooltip
            title={it.label}
            kicker={`${Math.round((it.count / total) * 100)}% OF ALL`}
            desc={it.tip}
            breakdownLabel={it.breakdownLabel}
            breakdown={it.breakdown}
            source={it.source || source}
            drill={it.onSelect || onSelect ? (drill || 'SEE THE ROWS →') : undefined}
            onDrill={onDrillRow}
          />
        </ChartTip>
      )}
    </div>
  );
}

/** A round photo (or initials). People are always round in the panel. */
export function Avatar({ url, initials, size = 32, className = '' }) {
  return (
    <span className={`inline-flex flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#F2F2F2] font-mono text-[9px] font-bold uppercase text-[#4B5563] ${className}`} style={{ width: size, height: size }}>
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : initials}
    </span>
  );
}

/** Tiny bar sparkline for a row: values oldest → newest, the newest one cyan. */
export function Sparkline({ values, width = 96, height = 18 }) {
  const max = Math.max(1, ...values);
  const n = Math.max(1, values.length);
  const bw = Math.max(1, Math.floor(width / n) - 1);
  return useMemo(() => (
    <svg width={width} height={height} aria-hidden="true">
      {values.map((v, i) => (
        <rect key={i} x={i * (bw + 1)} y={height - Math.max(2, (v / max) * height)} width={bw} height={Math.max(2, (v / max) * height)} rx="2" fill={i === values.length - 1 ? CYAN : MAGENTA} />
      ))}
    </svg>
  ), [values, width, height, bw, max]);
}
